import { type Observable, of } from 'rxjs';
import { catchError, filter, ignoreElements, map, mergeMap, tap } from 'rxjs/operators';

import { type Actions as ActionsAccount, ActionTypes as ActionTypesAccount } from '../accounts/actions';
import { type Actions as RootActions } from '../actions';
import { type RequestError } from '../shared';

import { ActionTypes, Actions as SettingsActions } from './actions';
import { getSettingsPrivate } from './api';
import { client } from './service';

const TRACK_ACCOUNT_SEARCH_METRIC = 'Used account search';
const TRACK_ACCOUNT_SEARCH_TIMING = 'Account search fetch time';

type AccountSearchActions =
  | ReturnType<typeof ActionsAccount.fetchAccounts>
  | ReturnType<typeof ActionsAccount.receiveAccounts>
  | ReturnType<typeof ActionsAccount.searchAccounts>
  | ReturnType<typeof ActionsAccount.receiveSearchResults>;

export const trackAccountSearch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (
        action,
      ): action is Extract<
        RootActions,
        { type: ActionTypesAccount.FETCH_ACCOUNTS_FULFILLED | ActionTypesAccount.RECEIVE_ACCOUNT_RESULTS }
      > =>
        action.type === ActionTypesAccount.FETCH_ACCOUNTS_FULFILLED ||
        action.type === ActionTypesAccount.RECEIVE_ACCOUNT_RESULTS,
    ),
    filter((action: AccountSearchActions) => !!action.q),
    tap(() => {
      if (!client) {
        return;
      }

      client.track(TRACK_ACCOUNT_SEARCH_METRIC);
    }),
    ignoreElements(),
  );

export const trackAccountSearchTiming = (action$: Observable<RootActions>) => {
  let startTime = 0;

  return action$.pipe(
    filter(
      (
        action,
      ): action is Extract<
        RootActions,
        {
          type:
            | ActionTypesAccount.FETCH_ACCOUNTS
            | ActionTypesAccount.SEARCH_ACCOUNTS
            | ActionTypesAccount.FETCH_ACCOUNTS_FULFILLED
            | ActionTypesAccount.RECEIVE_ACCOUNT_RESULTS;
        }
      > =>
        action.type === ActionTypesAccount.FETCH_ACCOUNTS ||
        action.type === ActionTypesAccount.SEARCH_ACCOUNTS ||
        action.type === ActionTypesAccount.FETCH_ACCOUNTS_FULFILLED ||
        action.type === ActionTypesAccount.RECEIVE_ACCOUNT_RESULTS,
    ),
    filter((action: AccountSearchActions) => !!action.q),
    tap((action: AccountSearchActions) => {
      // XXX: there's probably a better way to do this in rxjs-land, but I couldn't figure it out
      if (action.type === ActionTypesAccount.FETCH_ACCOUNTS || action.type === ActionTypesAccount.SEARCH_ACCOUNTS) {
        startTime = new Date().valueOf();
        return;
      }
      if (!client) {
        return;
      }
      const endTime = new Date().valueOf();

      client.track(TRACK_ACCOUNT_SEARCH_TIMING, undefined, endTime - startTime);
    }),
    ignoreElements(),
  );
};

export const settingsPrivateFetch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.UPDATE_SETTINGS_PRIVATE }> =>
        action.type === ActionTypes.UPDATE_SETTINGS_PRIVATE,
    ),
    mergeMap(() =>
      getSettingsPrivate().pipe(
        map((response) => SettingsActions.updateSettingsPrivateFulfilled(response)),
        catchError((error: RequestError) => of(SettingsActions.updateSettingsPrivateFailed(error))),
      ),
    ),
  );
